import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';

const callForJson = async <T>(endpoint: string, json?: any): Promise<T> => {
  return call<T>(endpoint, { json }).then((res) => res.json());
};

export type RenewalStatus = 'pending' | 'invoiced' | 'paid' | 'cancelled';

export type TRenewal = {
  id: number;
  offerId: number;
  status: RenewalStatus;
  cycleNumber: number;
  renewalDate: string;
  issueDate: string;
  dueDate: string;
  invoiceId: number | null;
  createdAt: string;
  contactName: string;
  contactEmail: string;
  proposalName: string;
  jurisdiction: string;
  invoiceNumber: string | null;
  invoiceAmount: number | null;
  invoiceCurrency: string | null;
  invoiceStatus: string | null;
};

export type TRenewalPosition = {
  id: number;
  renewalId: number;
  serviceId: number;
  serviceName: string;
  renewalPrice: number;
  renewalCurrency: string;
  intervalMonths: number;
  offsetDays: number;
  fineType: string | null;
  fineAmount: number;
  incrementValue: number;
  incrementInterval: string | null;
  serviceParentId: number | null;
  optOutUpdates: boolean;
  weight: number;
};

export type TRenewalHistory = {
  id: number;
  renewalId: number;
  invoiceId: number | null;
  positionId: number | null;
  action: string;
  field: string | null;
  oldValue: any;
  newValue: any;
  changedBy: number;
  changedByName: string;
  createdAt: string;
};

export type TRenewalDetails = {
  renewal: TRenewal & {
    anchorDate: string;
    shareCurrency: string;
    isAggregated: boolean;
    aggregationLabel: string | null;
    originalDueDate: string | null;
    invoicePaidAt: string | null;
  };
  positions: TRenewalPosition[];
  invoicePositions: any[];
  history: TRenewalHistory[];
};

export type RenewalStatusCounts = {
  all: number;
  pending: number;
  invoiced: number;
  paid: number;
  cancelled: number;
};

export interface RenewalListParams {
  page?: number;
  limit?: number;
  status?: RenewalStatus | 'all';
  offerId?: number;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'created_at' | 'renewal_date' | 'issue_date' | 'due_date';
  sortOrder?: 'asc' | 'desc';
}

export interface RenewalListResponse {
  renewals: TRenewal[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: RenewalStatusCounts;
}

const keys = {
  all: ['renewals'] as const,
  list: (params?: RenewalListParams) => [...keys.all, 'list', params] as const,
  details: (id: number) => [...keys.all, 'details', id] as const,
};

export const useRenewalList = (params?: RenewalListParams) => {
  const json: Record<string, any> = {};
  if (params?.page) json.page = params.page;
  if (params?.limit) json.limit = params.limit;
  if (params?.status && params.status !== 'all') json.status = params.status;
  if (params?.offerId) json.offerId = params.offerId;
  if (params?.dateFrom) json.dateFrom = params.dateFrom;
  if (params?.dateTo) json.dateTo = params.dateTo;
  if (params?.sortBy) json.sortBy = params.sortBy;
  if (params?.sortOrder) json.sortOrder = params.sortOrder;

  return useQuery(
    {
      queryKey: keys.list(params),
      queryFn: () =>
        call<RenewalListResponse>('renewals/list', {
          method: 'GET',
          json,
        }).then((res) => res.json()),
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    qc,
  );
};

export const useRenewalDetails = (id: number) => {
  return useQuery(
    {
      queryKey: keys.details(id),
      queryFn: () =>
        call<TRenewalDetails>('renewals/details', {
          method: 'GET',
          json: { id },
        }).then((res) => res.json()),
      enabled: id > 0,
    },
    qc,
  );
};

export const useCancelRenewal = () => {
  return useMutation(
    {
      mutationFn: (id: number) => callForJson<void>('renewals/cancel', { id }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );
};

// Invoicing companies
export type TInvoicingCompany = {
  id: number;
  code: string;
  name: string;
  registrationNumber: string | null;
  vatNumber: string | null;
  legalAddress: string;
  countryCode: string;
  vatRate: number;
  priority: number;
  isActive: boolean;
};

export const useInvoicingCompanies = () => {
  return useQuery(
    {
      queryKey: ['invoicing-companies'] as const,
      queryFn: () =>
        call<TInvoicingCompany[]>('invoicing/companies', {
          method: 'GET',
        }).then((res) => res.json()),
      staleTime: 30 * 60 * 1000,
    },
    qc,
  );
};

// Jurisdiction late fees
export type TJurisdictionFee = {
  id: number;
  jurisdiction: string;
  feeAmount: number;
  feeCurrency: string;
  createdAt: string;
  updatedAt: string;
};

const feeKeys = {
  all: ['jurisdiction-fees'] as const,
};

export const useJurisdictionFees = () => {
  return useQuery(
    {
      queryKey: feeKeys.all,
      queryFn: () =>
        call<TJurisdictionFee[]>('settings/jurisdiction-fees', {
          method: 'GET',
        }).then((res) => res.json()),
    },
    qc,
  );
};

export const useSaveJurisdictionFee = () => {
  return useMutation(
    {
      mutationFn: (data: {
        jurisdiction: string;
        feeAmount: number;
        feeCurrency: string;
      }) => callForJson<void>('settings/jurisdiction-fees', data),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: feeKeys.all });
      },
    },
    qc,
  );
};

export const useDeleteJurisdictionFee = () => {
  return useMutation(
    {
      mutationFn: (id: number) =>
        call('settings/jurisdiction-fees/delete', { json: { id } }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: feeKeys.all });
      },
    },
    qc,
  );
};

// Eligible shares for delivery tracking
export type TEligibleShare = {
  offerId: number;
  contactName: string;
  contactEmail: string;
  proposalName: string;
  jurisdiction: string;
  invoicePaidAt: string;
  anchorDate: string | null;
  totalServices: number;
  deliveredServices: number;
};

export const useEligibleShares = () => {
  return useQuery(
    {
      queryKey: [...keys.all, 'eligible-shares'] as const,
      queryFn: () =>
        call<{ shares: TEligibleShare[] }>('renewals/eligible-shares', {
          method: 'GET',
        }).then((res) => res.json()),
      staleTime: 2 * 60 * 1000,
    },
    qc,
  );
};

// Service delivery tracking
export type TDeliveryService = {
  serviceId: number;
  offerItemId: number | null;
  serviceName: string;
  isRenewable: boolean;
  parentId: number | null;
  deliveredAt: string | null;
  deliveredBy: number | null;
  deliveredByName: string | null;
};

export type TDeliveryBlock = {
  blockTitle: string;
  blockType: string;
  tierLabel: string | null;
  services: TDeliveryService[];
};

export type TDeliveryStatus = {
  blocks: TDeliveryBlock[];
  standaloneServices: TDeliveryService[];
  anchorDate: string | null;
};

export type TDeliverResult = {
  delivered: boolean;
};

export const useDeliveryStatus = (offerId: number) => {
  return useQuery(
    {
      queryKey: [...keys.all, 'delivery-status', offerId] as const,
      queryFn: () =>
        call<TDeliveryStatus>('renewals/delivery-status', {
          method: 'GET',
          json: { offerId },
        }).then((res) => res.json()),
      enabled: offerId > 0,
    },
    qc,
  );
};

export const useDeliverService = () => {
  return useMutation(
    {
      mutationFn: (data: {
        offerId: number;
        serviceId: number;
        offerItemId: number;
      }) => callForJson<TDeliverResult>('renewals/deliver-service', data),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );
};
